import { PLATFORM_TENANT, principalFromJwtClaims, toPrincipal, } from '@platform/authz';
export function activeWorkspace(principal) {
    if (!principal.tenantId)
        return null;
    return principal.workspaces.find((w) => w.tenantId === principal.tenantId) ?? null;
}
/** Platform staff act in the literal tenant `platform` (docs/authz.md §1). */
export function isPlatformPrincipal(principal) {
    return principal.tenantId === PLATFORM_TENANT;
}
/** The principal re-scoped to one of its workspaces: tenant, roles, office, agent and grants. */
export function withActiveTenant(principal, tenantId) {
    const workspace = principal.workspaces.find((w) => w.tenantId === tenantId);
    if (!workspace)
        throw new Error(`Not a member of workspace ${tenantId}`);
    const base = toPrincipal({
        sub: principal.sub,
        tenantId,
        tier: workspace.tier,
        memberships: principal.memberships.map(stripWorkspace),
        roles: workspace.roles,
        ...(workspace.officeCode !== undefined ? { officeCode: workspace.officeCode } : {}),
        ...(workspace.agentCode !== undefined ? { agentCode: workspace.agentCode } : {}),
        grants: workspace.grants ?? [],
    });
    return {
        ...base,
        name: principal.name,
        email: principal.email,
        workspaces: principal.workspaces,
    };
}
/** A principal with no active tenant (the picker decides). */
export function withoutActiveTenant(principal) {
    const base = toPrincipal({
        sub: principal.sub,
        tier: principal.tier,
        memberships: principal.memberships.map(stripWorkspace),
        roles: [],
        grants: [],
    });
    return {
        ...base,
        name: principal.name,
        email: principal.email,
        workspaces: principal.workspaces,
    };
}
function stripWorkspace(m) {
    return {
        tenantId: m.tenantId,
        roles: [...m.roles],
        ...(m.officeCode !== undefined ? { officeCode: m.officeCode } : {}),
        ...(m.agentCode !== undefined ? { agentCode: m.agentCode } : {}),
        ...(m.grants !== undefined ? { grants: [...m.grants] } : {}),
    };
}
/** Replace the display data of the workspaces (from `GET /me/memberships`), keeping the rest. */
export function withWorkspaceDetails(principal, details) {
    const byId = new Map(details.map((d) => [d.tenantId, d]));
    const workspaces = principal.workspaces.map((w) => {
        const d = byId.get(w.tenantId);
        if (!d)
            return w;
        return { ...w, tenantName: d.tenantName, tier: d.tier, ...(d.brand ? { brand: d.brand } : {}) };
    });
    const active = principal.tenantId
        ? workspaces.find((w) => w.tenantId === principal.tenantId)
        : undefined;
    return { ...principal, workspaces, ...(active ? { tier: active.tier } : {}) };
}
function stringClaim(claims, key) {
    const v = claims[key];
    return typeof v === 'string' && v.trim().length > 0 ? v.trim() : undefined;
}
function displayName(claims, email) {
    const name = stringClaim(claims, 'name');
    if (name)
        return name;
    const parts = [stringClaim(claims, 'given_name'), stringClaim(claims, 'family_name')].filter((p) => Boolean(p));
    return parts.length > 0 ? parts.join(' ') : email;
}
/**
 * The ID-token claims of a CELL pool user → an `AppPrincipal` with NO active tenant. Roles
 * live in `custom:memberships` (identity lane rule: cell users are not in Cognito groups);
 * `custom:tenantId` is the home tenant, a hint the adapter uses when choosing the active one.
 * Workspace names are not in the token — `withWorkspaceDetails` adds them from the API.
 */
export function tenantPrincipalFromClaims(claims) {
    const base = principalFromJwtClaims(claims);
    const email = stringClaim(claims, 'email') ?? '';
    const workspaces = base.memberships.map((m) => ({
        ...m,
        tenantName: m.tenantId,
        tier: base.tier,
    }));
    const anonymous = toPrincipal({
        sub: base.sub,
        tier: base.tier,
        memberships: base.memberships.map(stripWorkspace),
        roles: [],
        grants: [],
    });
    return { ...anonymous, name: displayName(claims, email), email, workspaces };
}
/** The home tenant hint carried by the token (`custom:tenantId`), if it is a membership. */
export function homeTenantOf(claims, principal) {
    const home = stringClaim(claims, 'custom:tenantId');
    return home && principal.workspaces.some((w) => w.tenantId === home) ? home : undefined;
}
/**
 * The ID-token claims of a PLATFORM-ADMIN pool user → a principal acting in the `platform`
 * tenant. Roles come from `cognito:groups` (PlatformAdmin); anything else is dropped by authz.
 */
export function platformPrincipalFromClaims(claims) {
    const base = principalFromJwtClaims({ ...claims, 'custom:tenantId': PLATFORM_TENANT });
    const email = stringClaim(claims, 'email') ?? '';
    return { ...base, name: displayName(claims, email), email, workspaces: [] };
}
//# sourceMappingURL=principal.js.map